import { useAuthStore } from '@stores/authStore';
import { usersMutations } from '@therame/api-client';
import { OfficeLocationFullSizeDto, PayedFeatureType } from '@therame/shared';
import { defineStore } from 'pinia';
import { Subject, type Observable } from 'rxjs';
import { ref, type Ref } from 'vue';

type OfficeLocationStore = {
  allOfficeLocations: Ref<OfficeLocationFullSizeDto[]>;
  switchOfficeLocationTrigger: Observable<void>;
  setCurrentOfficeLocation: (officeLocationId: string) => Promise<void>;
  findOfficeLocationById: (
    officeLocationId: string | null,
  ) => OfficeLocationFullSizeDto | undefined;
  isEntitledTo: (feature: PayedFeatureType) => boolean;
  isEntitledToOne: (features: PayedFeatureType[]) => boolean;
};

export const useOfficeLocationStore = defineStore('officeLocation', (): OfficeLocationStore => {
  const allOfficeLocations = ref<OfficeLocationFullSizeDto[]>([]);
  const switchOfficeLocationTrigger = ref<Subject<void>>(new Subject<void>());

  const findOfficeLocationById = (officeLocationId: string | null) =>
    allOfficeLocations.value.find((officeLocation) => officeLocation.id === officeLocationId);

  const setCurrentOfficeLocation = async (officeLocationId: string) => {
    const authStore = useAuthStore();
    if (!authStore.authContext) return;

    await usersMutations.patch(authStore.authContext.user.id!, {
      currentOfficeLocationId: officeLocationId,
    });

    await authStore.refreshAuthContext();
    switchOfficeLocationTrigger.value.next();
  };

  const isEntitledTo = (feature: PayedFeatureType) => {
    const authStore = useAuthStore();
    if (!authStore.authContext?.user.currentOfficeLocation) return false;
    return authStore.authContext.user.currentOfficeLocation.payedFeatures.some(
      (payedFeature) => payedFeature.type === feature,
    );
  };

  const isEntitledToOne = (features: PayedFeatureType[]) => {
    const authStore = useAuthStore();
    if (!features) return true;
    if (!authStore.authContext?.user.currentOfficeLocation?.payedFeatures) return false;
    return authStore.authContext.user.currentOfficeLocation.payedFeatures.some((payedFeature) =>
      features.includes(payedFeature.type),
    );
  };

  return {
    allOfficeLocations,
    switchOfficeLocationTrigger: switchOfficeLocationTrigger.value.asObservable(),
    setCurrentOfficeLocation,
    findOfficeLocationById,
    isEntitledTo,
    isEntitledToOne,
  };
});
